import React, { useState, useEffect } from "react";
import { Row, Col, Card, Table, Alert, ProgressBar, Badge } from "react-bootstrap";
import _ from "lodash";
import axios from "axios";
import Utils from "../../common/Utils";
import moment from "moment";

const AttendanceFlags = ({ row }) => {
  const flags = [];

  // Check-In Required
  if (!row.check_in_time) {
    flags.push({
      id: 'checkin',
      icon: 'bi-exclamation-circle-fill',
      text: 'Check-In Required',
      color: 'danger'
    });
  }
  // Check-Out Required (only if check-in exists)
  else if (row.check_in_time && !row.check_out_time) {
    flags.push({
      id: 'checkout',
      icon: 'bi-clock-fill',
      text: 'Check-Out Required',
      color: 'warning'
    });
  }
  // Short Attendance (check-in and check-out exist but duration < 1 min)
  else if (row.check_in_time && row.check_out_time) {
    const checkIn = moment(row.check_in_time);
    const checkOut = moment(row.check_out_time);
    const durationMinutes = checkOut.diff(checkIn, 'minutes');
    
    if (durationMinutes < 1) {
      flags.push({
        id: 'short',
        icon: 'bi-exclamation-triangle-fill',
        text: 'Short Attendance (< 1 min)',
        color: 'danger'
      });
    }
  }

  if (flags.length === 0) return null;

  return (
    <div className="d-flex flex-wrap gap-2">
      {flags.map(flag => (
        <Badge key={flag.id} bg={flag.color} className="d-flex align-items-center gap-1" style={{ fontSize: '0.75rem' }}>
          <i className={`bi ${flag.icon}`} style={{ fontSize: '0.85rem' }}></i>
          <span>{flag.text}</span>
        </Badge>
      ))}
    </div>
  );
};

const MySummary = ({ summary, refreshTrigger }) => {
  const [checkinRows, setCheckinRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 10;

  const fetchSummary = () => {
    setLoading(true);
    const from = moment().startOf('month').format('YYYY-MM-DD');
    const to = moment().endOf('month').format('YYYY-MM-DD');
    axios.get(Utils.apiUrl(`attendence/checkinsummary?from=${from}&to=${to}`), Utils.apiHeaders())
      .then(res => {
        const data = Utils.parseApiResponse(res);
        if (data.success) {
          setCheckinRows(data.data || []);
          setCurrentPage(1);
        }
      })
      .catch(() => { })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchSummary();
  }, [refreshTrigger]);

  const totalPages = Math.ceil(checkinRows.length / perPage);
  const paginatedRows = checkinRows.slice((currentPage - 1) * perPage, currentPage * perPage);

  const renderPagination = () => {
    if (checkinRows.length <= perPage) return null;
    return (
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 p-3 border-top bg-light">
        <span className="small text-muted fw-semibold">
          Showing {(currentPage - 1) * perPage + 1} to {Math.min(currentPage * perPage, checkinRows.length)} of {checkinRows.length} records
        </span>
        <div className="d-flex align-items-center gap-1">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary rounded-pill px-3"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            ← Previous
          </button>
          <span className="px-2 small fw-bold text-dark">
            Page {currentPage} of {totalPages}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary rounded-pill px-3"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next →
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="my-3">
      {/* Yearly Attendance KPIs */}
      <section className="dt-card">
        <div className="dt-card__head">
          <h2 className="dt-card__title">My Attendance Summary (Year to Date)</h2>
        </div>
        <div className="streaks-grid">
          <div className="dt-streak">
            <div className="dt-streak__ico" style={{ background: "var(--mint-wash)", color: "var(--mint)" }}>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12.5 4.5 4.5L19 7"/></svg>
            </div>
            <div className="dt-streak__body">
              <div className="dt-streak__n" style={{ color: "var(--mint)" }}>
                {_.get(summary, "ontime", 0) || 0}
              </div>
              <div className="dt-streak__l">On Time (Clock-ins)</div>
            </div>
          </div>

          <div className="dt-streak">
            <div className="dt-streak__ico" style={{ background: "var(--coral-wash)", color: "var(--coral)" }}>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
            </div>
            <div className="dt-streak__body">
              <div className="dt-streak__n" style={{ color: "var(--coral)" }}>
                {_.get(summary, "differed", 0) || 0}
              </div>
              <div className="dt-streak__l">Differed Time Events</div>
            </div>
          </div>

          <div className="dt-streak">
            <div className="dt-streak__ico" style={{ background: "var(--brand-wash)", color: "var(--dt-action)" }}>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3.2 1.9"/></svg>
            </div>
            <div className="dt-streak__body">
              <div className="dt-streak__n" style={{ color: "var(--dt-action)" }}>
                {_.get(summary, "total", 0) || 0}
              </div>
              <div className="dt-streak__l">Total Worked Hours</div>
            </div>
          </div>
        </div>
      </section>

      {/* Monthly Check-In / Out Logs */}
      <section className="dt-card dt-card--flush">
        <div className="dt-card__head d-flex justify-content-between align-items-center" style={{ padding: "16px 20px 0", marginBottom: "12px" }}>
          <h2 className="dt-card__title">This Month – Check-In / Check-Out Log</h2>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1 rounded-pill px-3 py-1 fw-semibold"
            style={{ fontSize: "12px" }}
            onClick={fetchSummary}
            disabled={loading}
          >
            <i className={`bi bi-arrow-clockwise ${loading ? "spin" : ""}`} />
            <span>{loading ? "Refreshing..." : "Refresh"}</span>
          </button>
        </div>
        <div>
          {loading && (
            <div className="p-4 text-center">
              <ProgressBar animated now={100} variant="primary" style={{ height: "6px" }} />
              <p className="mt-2 text-muted small">Loading monthly records...</p>
            </div>
          )}

          {!loading && checkinRows.length > 0 && (
            <>
              {/* Desktop Table View */}
              <div className="table-responsive d-none d-md-block">
                <table className="dt-table m-0">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Selfie</th>
                      <th>Check-In</th>
                      <th>Check-Out</th>
                      <th>Location / GPS</th>
                      <th>Status</th>
                      <th>Flags</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRows.map((row, idx) => {
                      let statusBadge = <span className="chip chip--neutral">—</span>;
                      if (row.ontime === 1) {
                        if (row.missing_checkout == 1) {
                          statusBadge = <span className="chip chip--over">Missing Checkout</span>;
                        } else if (row.check_out_time) {
                          statusBadge = <span className="chip chip--ok">Checked Out</span>;
                        } else if (row.check_in_time) {
                          const checkInTime = moment(row.check_in_time);
                          const limit = moment(row.check_in_time).set({ hour: 9, minute: 15, second: 0 });
                          if (checkInTime.isAfter(limit)) {
                            statusBadge = <span className="chip chip--due">Late</span>;
                          } else {
                            statusBadge = <span className="chip chip--ok">Working</span>;
                          }
                        }
                      } else {
                        if (row.approved == 1) {
                          statusBadge = <span className="chip chip--ok">Approved</span>;
                        } else if (row.approved_at) {
                          statusBadge = <span className="chip chip--over">Rejected</span>;
                        } else {
                          statusBadge = <span className="chip chip--due">Pending</span>;
                        }
                      }

                      return (
                        <tr key={idx}>
                          <td><strong>{moment(row.date).format("DD-MMM")}</strong></td>
                          <td>
                            <div className="d-flex align-items-center gap-1">
                              {row.check_in_image && (
                                <img
                                  src={row.check_in_image}
                                  alt="selfie in"
                                  style={{ width: 34, height: 34, objectFit: "cover", borderRadius: 8, cursor: "pointer", border: "1px solid var(--line)" }}
                                  onClick={() => Utils.viewImage(row.check_in_image)}
                                  title="Check-in Selfie"
                                />
                              )}
                              {row.check_out_image && (
                                <img
                                  src={row.check_out_image}
                                  alt="selfie out"
                                  style={{ width: 34, height: 34, objectFit: "cover", borderRadius: 8, cursor: "pointer", border: "1px solid var(--line)" }}
                                  onClick={() => Utils.viewImage(row.check_out_image)}
                                  title="Check-out Selfie"
                                />
                              )}
                              {!row.check_in_image && !row.check_out_image && <span className="text-muted">—</span>}
                            </div>
                          </td>
                          <td className="num">{row.check_in_time ? moment(row.check_in_time).format("hh:mm A") : "—"}</td>
                          <td className="num">{row.check_out_time ? moment(row.check_out_time).format("hh:mm A") : "—"}</td>
                          <td>
                            <small className="text-muted d-block">
                              {row.address || (row.latitude ? `${parseFloat(row.latitude).toFixed(3)}, ${parseFloat(row.longitude).toFixed(3)}` : "—")}
                            </small>
                          </td>
                          <td>{statusBadge}</td>
                          <td><AttendanceFlags row={row} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card List View */}
              <div className="d-md-none p-3 d-flex flex-column gap-3">
                {paginatedRows.map((row, idx) => {
                  let statusBadge = <span className="chip chip--neutral">—</span>;
                  if (row.ontime === 1) {
                    if (row.missing_checkout == 1) {
                      statusBadge = <span className="chip chip--over">Missing Checkout</span>;
                    } else if (row.check_out_time) {
                      statusBadge = <span className="chip chip--ok">Checked Out</span>;
                    } else if (row.check_in_time) {
                      const checkInTime = moment(row.check_in_time);
                      const limit = moment(row.check_in_time).set({ hour: 9, minute: 15, second: 0 });
                      if (checkInTime.isAfter(limit)) {
                        statusBadge = <span className="chip chip--due">Late</span>;
                      } else {
                        statusBadge = <span className="chip chip--ok">Working</span>;
                      }
                    }
                  } else {
                    if (row.approved == 1) {
                      statusBadge = <span className="chip chip--ok">Approved</span>;
                    } else if (row.approved_at) {
                      statusBadge = <span className="chip chip--over">Rejected</span>;
                    } else {
                      statusBadge = <span className="chip chip--due">Pending</span>;
                    }
                  }

                  return (
                    <div key={idx} className="dt-mobile-record-card">
                      <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                        <span className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                          📅 {moment(row.date).format("DD MMMM YYYY")}
                        </span>
                        {statusBadge}
                      </div>

                      <div className="row g-2 mb-2">
                        <div className="col-6">
                          <div className="p-2 rounded bg-light border">
                            <div className="text-muted small" style={{ fontSize: "10.5px", fontWeight: 600 }}>CHECK-IN</div>
                            <div className="fw-bold num text-success" style={{ fontSize: "13.5px" }}>
                              {row.check_in_time ? moment(row.check_in_time).format("hh:mm A") : "—"}
                            </div>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="p-2 rounded bg-light border">
                            <div className="text-muted small" style={{ fontSize: "10.5px", fontWeight: 600 }}>CHECK-OUT</div>
                            <div className="fw-bold num text-danger" style={{ fontSize: "13.5px" }}>
                              {row.check_out_time ? moment(row.check_out_time).format("hh:mm A") : "—"}
                            </div>
                          </div>
                        </div>
                      </div>

                      {(row.check_in_image || row.check_out_image) && (
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <span className="text-muted small" style={{ fontSize: "11px" }}>Selfies:</span>
                          {row.check_in_image && (
                            <img
                              src={row.check_in_image}
                              alt="selfie in"
                              style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 8, cursor: "pointer", border: "1.5px solid #CBD5E1" }}
                              onClick={() => Utils.viewImage(row.check_in_image)}
                              title="Check-in Selfie"
                            />
                          )}
                          {row.check_out_image && (
                            <img
                              src={row.check_out_image}
                              alt="selfie out"
                              style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 8, cursor: "pointer", border: "1.5px solid #CBD5E1" }}
                              onClick={() => Utils.viewImage(row.check_out_image)}
                              title="Check-out Selfie"
                            />
                          )}
                        </div>
                      )}

                      <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
                        <small className="text-muted text-truncate" style={{ maxWidth: "60%" }}>
                          📍 {row.address || (row.latitude ? `${parseFloat(row.latitude).toFixed(3)}, ${parseFloat(row.longitude).toFixed(3)}` : "Location Verified")}
                        </small>
                        <AttendanceFlags row={row} />
                      </div>
                    </div>
                  );
                })}
              </div>

              {renderPagination()}
            </>
          )}

          {!loading && checkinRows.length === 0 && (
            <div className="alert alert-info m-3 p-3">
              No attendance records found for this month.
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default MySummary;
